/**
 * Alpine-компонент authDialogs — единая <dialog>-модалка для login/forgot/reset/success/token-expired.
 *
 * Открытие через события:
 *   document.dispatchEvent(new CustomEvent('login:open'))
 *   document.dispatchEvent(new CustomEvent('password:forgot'))
 *   document.dispatchEvent(new CustomEvent('password:reset', { detail: { token, email } }))
 *   document.dispatchEvent(new CustomEvent('password:reset-success'))
 *   document.dispatchEvent(new CustomEvent('password:token-expired'))
 *
 * Заменяет 5 FancyBox-модалок:
 *   login-modal, forgot-password-modal, reset-password-modal,
 *   password-reset-success-modal, token-expired-modal.
 *
 * Backend endpoints:
 *   POST /auth/ajax                 — логин (unified error, 10/min)
 *   POST /password/forgot-ajax      — запрос reset-ссылки (5/min)
 *   POST /password/reset-ajax       — установка нового пароля (5/min)
 */

import Alpine from 'alpinejs';
import { apiRequest, APIError } from '../../api.js';
import { openDialog, closeDialog, focusFirstInput } from '../../dialog-utils.js';

const PASSWORD_HINT_ERRORS = {
    LENGTH: 'at least 8 characters',
    LOWERCASE: 'one lowercase letter',
    NUMBER: 'one number',
};

Alpine.data('authDialogs', () => ({
    mode: null, // 'login' | 'forgot' | 'reset' | 'reset-success' | 'token-expired'
    submitting: false,

    // Login
    login: {
        email: '',
        password: '',
        errors: {},
        showPassword: false,
    },

    // Forgot password
    forgot: {
        email: '',
        successMessage: '',
        errors: {},
    },

    // Reset password (из ссылки email)
    reset: {
        token: '',
        email: '',
        password: '',
        password_confirmation: '',
        passwordHint: '',
        errors: {},
        tokenExpiredMessage: '',
        showPassword: false,
        showPasswordConfirmation: false,
    },

    // Success countdown
    successCountdown: 3,
    _successInterval: null,

    init() {
        // Обработчики глобальных событий открытия
        document.addEventListener('login:open', () => this.openLogin());
        document.addEventListener('password:forgot', () => this.openForgot());
        document.addEventListener('password:reset', (e) => {
            const detail = e.detail || {};
            this.openReset(detail.token || '', detail.email || '');
        });
        document.addEventListener('password:reset-success', () => this.openResetSuccess());
        document.addEventListener('password:token-expired', () => this.openTokenExpired());

        // Cleanup state ТОЛЬКО после полного закрытия (после анимации). Native <dialog>
        // диспатчит 'close' в момент close() — это единая точка для всех путей закрытия:
        // компонент.close(), Esc, клик по бэкдропу.
        this.$refs.dialog?.addEventListener('close', () => {
            this._clearSuccessInterval();
            this.mode = null;
        });

        // Автофокус при смене режима (login → forgot → reset). openDialog тоже
        // фокусирует, но если пользователь переключается между режимами в открытом
        // диалоге — нужно сфокусировать новый видимый input.
        this.$watch('mode', () => {
            if (this.mode) this.$nextTick(() => focusFirstInput(this.$refs.dialog));
        });
    },

    openLogin() {
        this.mode = 'login';
        this.login.errors = {};
        openDialog(this.$refs.dialog);
    },

    openForgot() {
        this.mode = 'forgot';
        this.forgot.errors = {};
        this.forgot.successMessage = '';
        openDialog(this.$refs.dialog);
    },

    openReset(token, email) {
        this.mode = 'reset';
        this.reset.token = token;
        this.reset.email = email;
        this.reset.password = '';
        this.reset.password_confirmation = '';
        this.reset.errors = {};
        this.reset.passwordHint = '';
        openDialog(this.$refs.dialog);
    },

    openResetSuccess() {
        this.mode = 'reset-success';
        this.successCountdown = 3;
        this._clearSuccessInterval();
        openDialog(this.$refs.dialog);

        this._successInterval = setInterval(() => {
            this.successCountdown--;
            if (this.successCountdown <= 0) {
                this._clearSuccessInterval();
                setTimeout(() => this.openLogin(), 200);
            }
        }, 1000);
    },

    openTokenExpired() {
        this.mode = 'token-expired';
        openDialog(this.$refs.dialog);
    },

    close() {
        // Запускаем анимацию закрытия. Cleanup state (mode = null, clearInterval) —
        // в обработчике 'close' event на dialog (см. init()), чтобы контент не пропадал
        // до того, как анимация исчезновения отыграет.
        closeDialog(this.$refs.dialog);
    },

    _clearSuccessInterval() {
        if (this._successInterval) {
            clearInterval(this._successInterval);
            this._successInterval = null;
        }
    },

    // ─── Login submit ──────────────────────────────────────────────────
    async submitLogin() {
        if (this.submitting) return;

        this.login.errors = {};
        this.submitting = true;

        try {
            const formData = new FormData();
            formData.append('email', this.login.email);
            formData.append('password', this.login.password);

            const response = await apiRequest('/auth/ajax', {
                method: 'POST',
                body: formData,
            });

            if (response && response.success) {
                if (window.location.pathname.includes('/password/reset/')) {
                    window.location.href = '/';
                } else {
                    window.location.reload();
                }
            } else {
                const msg = (response && response.message) || 'Invalid email or password';
                this.login.errors.email = msg;
                window.showToast('error', msg);
            }
        } catch (e) {
            if (e instanceof APIError && e.status === 422 && e.details?.fields) {
                Object.keys(e.details.fields).forEach(field => {
                    this.login.errors[field] = e.details.fields[field][0];
                });
                Object.values(this.login.errors).forEach(msg => window.showToast('error', msg));
            } else if (e instanceof APIError && e.status === 401) {
                const msg = e.message || 'Invalid email or password';
                this.login.errors.email = msg;
                window.showToast('error', msg);
            } else {
                const msg = e instanceof APIError ? e.message : 'An error occurred. Please try again later.';
                this.login.errors.email = msg;
                if (e instanceof APIError) window.showToast('error', msg);
            }
        } finally {
            this.submitting = false;
        }
    },

    // ─── Forgot password submit ────────────────────────────────────────
    async submitForgot() {
        if (this.submitting) return;

        this.forgot.errors = {};
        this.submitting = true;

        try {
            const formData = new FormData();
            formData.append('email', this.forgot.email);

            const response = await apiRequest('/password/forgot-ajax', {
                method: 'POST',
                body: formData,
            });

            if (response && response.success) {
                this.forgot.successMessage = response.message
                    || 'Reset link has been sent to your email address. Please check your email.';

                // Закрыть модалку через 5 секунд
                setTimeout(() => {
                    if (this.mode === 'forgot') {
                        this.close();
                        this.forgot.email = '';
                        this.forgot.successMessage = '';
                    }
                }, 5000);
            }
        } catch (e) {
            let message = 'An error occurred. Please try again.';
            if (e instanceof APIError) {
                if (e.status === 404) {
                    message = e.message || 'We could not find a user with that email address.';
                } else if (e.status === 422 && e.details?.fields?.email) {
                    message = e.details.fields.email[0];
                } else {
                    message = e.message || message;
                }
            }
            this.forgot.errors.email = message;
        } finally {
            this.submitting = false;
        }
    },

    // ─── Reset password submit ─────────────────────────────────────────
    async submitReset() {
        if (this.submitting) return;

        this.reset.errors = {};
        this.submitting = true;

        try {
            const formData = new FormData();
            formData.append('token', this.reset.token);
            formData.append('email', this.reset.email);
            formData.append('password', this.reset.password);
            formData.append('password_confirmation', this.reset.password_confirmation);

            const response = await apiRequest('/password/reset-ajax', {
                method: 'POST',
                body: formData,
            });

            if (response && response.success) {
                this.close();
                // Маленькая задержка для визуального эффекта перехода между модалками
                setTimeout(() => this.openResetSuccess(), 200);
            }
        } catch (e) {
            if (e instanceof APIError) {
                const fields = e.details?.fields;

                if (e.status === 422 && fields) {
                    if (fields.email) this.reset.errors.email = fields.email[0];
                    if (fields.password) {
                        this.reset.errors.password = fields.password[0];
                        const pwErr = fields.password[0].toLowerCase();
                        if (pwErr.includes('8 characters') || pwErr.includes('letter') || pwErr.includes('number')) {
                            this.reset.passwordHint = fields.password[0];
                        }
                    }
                    if (fields.token) {
                        this.reset.errors.email = 'Invalid or expired reset token. Please request a new password reset link.';
                    }
                } else if (e.status === 400 && e.message) {
                    const msg = e.message.toLowerCase();
                    if (msg.includes('token') || msg.includes('expired')) {
                        this.reset.tokenExpiredMessage = e.message;
                        this.reset.errors.email = e.message;
                    } else {
                        this.reset.errors.email = e.message;
                    }
                } else {
                    this.reset.errors.email = e.message || 'An error occurred while resetting your password. Please try again.';
                }
            } else {
                this.reset.errors.email = 'An error occurred while resetting your password. Please try again.';
            }
        } finally {
            this.submitting = false;
        }
    },

    // Подсказка требований к паролю при blur (reset)
    validatePasswordHint() {
        const password = this.reset.password;
        if (password.length === 0) {
            this.reset.passwordHint = '';
            return;
        }

        const errors = [];
        if (password.length < 8) errors.push(PASSWORD_HINT_ERRORS.LENGTH);
        if (!/[a-z]/.test(password)) errors.push(PASSWORD_HINT_ERRORS.LOWERCASE);
        if (!/[0-9]/.test(password)) errors.push(PASSWORD_HINT_ERRORS.NUMBER);

        this.reset.passwordHint = errors.length > 0
            ? 'Password must contain: ' + errors.join(', ')
            : '';
    },

    // Switch между модальными формами
    switchToRegister() {
        this.close();
        setTimeout(() => document.dispatchEvent(new CustomEvent('register:open')), 200);
    },

    switchToForgot() {
        this.close();
        setTimeout(() => this.openForgot(), 200);
    },

    switchToLogin() {
        this.close();
        setTimeout(() => this.openLogin(), 200);
    },
}));
